import { useState, useEffect, useMemo, useCallback } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { currencyApi, type Currency, type CreateCurrencyPayload, type UpdateCurrencyPayload } from '@/api/currencyApi';
import { mockCurrencies } from '@/mocks/finance';

export default function CurrencyPage() {
  const toast = useToast();

  // ── States ────────────────────────────────────────────────────────────────
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');

  // Modals
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [editModal, setEditModal] = useState<Currency | null>(null);
  const [rateModal, setRateModal] = useState<Currency | null>(null);
  const [deleteModal, setDeleteModal] = useState<Currency | null>(null);

  // Form states
  const [newCurrency, setNewCurrency] = useState<CreateCurrencyPayload>({
    code: '',
    name: '',
    symbol: '',
    exchangeRate: 0,
    isActive: true,
  });

  const [editFormData, setEditFormData] = useState<UpdateCurrencyPayload>({
    code: '',
    name: '',
    symbol: '',
    exchangeRate: 0,
    isActive: true,
  });

  const [newRateValue, setNewRateValue] = useState<number>(0);

  // ── Fetch Currencies ──────────────────────────────────────────────────────
  const fetchCurrencies = useCallback(async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) setRefreshing(true);
    try {
      const data = await currencyApi.getAll();
      setCurrencies(data);
    } catch (err: unknown) {
      console.warn('API error while fetching currencies, falling back to cached/mock data:', err);
      // Resilient fallback to mockCurrencies if server or network unreachable
      if (currencies.length === 0) {
        const fallbackList: Currency[] = mockCurrencies.map((mc) => ({
          ...mc,
          isBaseCurrency: mc.code === 'INR',
        }));
        setCurrencies(fallbackList);
      }
      toast.error('Could not connect to live currency server. Displaying local data.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [toast, currencies.length]);

  useEffect(() => {
    fetchCurrencies();
  }, [fetchCurrencies]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  // 1. Create Currency
  const handleAddCurrency = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = newCurrency.code.trim().toUpperCase();
    const name = newCurrency.name.trim();
    const exchangeRate = Number(newCurrency.exchangeRate);

    if (!code || code.length < 2 || code.length > 5) {
      toast.error('Currency Code must be between 2 and 5 characters (e.g. CAD, JPY)');
      return;
    }
    if (!name) {
      toast.error('Please enter a currency name');
      return;
    }
    if (isNaN(exchangeRate) || exchangeRate <= 0) {
      toast.error('Exchange rate must be greater than 0');
      return;
    }

    setSubmitting(true);
    try {
      const created = await currencyApi.create({
        code,
        name,
        symbol: newCurrency.symbol?.trim() || '',
        exchangeRate,
        isActive: newCurrency.isActive ?? true,
      });

      setCurrencies((prev) => [...prev, created]);
      toast.success(`${created.code} added successfully!`);
      setShowAddModal(false);
      setNewCurrency({ code: '', name: '', symbol: '', exchangeRate: 0, isActive: true });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to add currency';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // 2. Open Edit Modal
  const handleOpenEdit = (curr: Currency) => {
    setEditModal(curr);
    setEditFormData({
      code: curr.code,
      name: curr.name,
      symbol: curr.symbol,
      exchangeRate: curr.exchangeRate,
      isActive: curr.isActive,
    });
  };

  // Save Full Edit
  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editModal) return;

    const name = (editFormData.name || '').trim();
    const exchangeRate = Number(editFormData.exchangeRate);

    if (!name) {
      toast.error('Currency name cannot be empty');
      return;
    }
    if (isNaN(exchangeRate) || exchangeRate <= 0) {
      toast.error('Exchange rate must be greater than 0');
      return;
    }

    setSubmitting(true);
    try {
      const updated = await currencyApi.update(editModal.id, {
        code: editModal.isBaseCurrency ? editModal.code : editFormData.code?.trim().toUpperCase(),
        name,
        symbol: editFormData.symbol?.trim() || '',
        exchangeRate: editModal.isBaseCurrency ? 1.0 : exchangeRate,
        isActive: editModal.isBaseCurrency ? true : editFormData.isActive,
      });

      setCurrencies((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      toast.success(`${updated.code} updated successfully!`);
      setEditModal(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update currency';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // 3. Quick Update Exchange Rate
  const handleOpenRateModal = (curr: Currency) => {
    if (curr.isBaseCurrency) {
      toast.info('Base currency rate is pegged to 1.0000');
      return;
    }
    setRateModal(curr);
    setNewRateValue(curr.exchangeRate);
  };

  const handleSaveRate = async () => {
    if (!rateModal || newRateValue <= 0) {
      toast.error('Please enter a valid rate greater than 0');
      return;
    }

    setSubmitting(true);
    try {
      const updated = await currencyApi.updateRate(rateModal.id, newRateValue);
      setCurrencies((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      toast.success(`${updated.code} rate updated to ₹${updated.exchangeRate.toFixed(2)}`);
      setRateModal(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update rate';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // 4. Toggle Active Status
  const handleToggleActive = async (curr: Currency) => {
    if (curr.isBaseCurrency) {
      toast.info('Base currency (INR) must always remain active');
      return;
    }

    try {
      const updated = await currencyApi.toggleStatus(curr.id);
      setCurrencies((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      toast.success(`${updated.code} is now ${updated.isActive ? 'Active' : 'Inactive'}`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to toggle status';
      toast.error(msg);
    }
  };

  // 5. Delete Currency
  const handleOpenDeleteModal = (curr: Currency) => {
    if (curr.isBaseCurrency) {
      toast.error('Base currency (INR) cannot be deleted');
      return;
    }
    setDeleteModal(curr);
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal) return;
    setSubmitting(true);
    try {
      await currencyApi.delete(deleteModal.id);
      setCurrencies((prev) => prev.filter((c) => c.id !== deleteModal.id));
      toast.success(`Currency ${deleteModal.code} deleted successfully`);
      setDeleteModal(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to delete currency';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Filtered Currencies ───────────────────────────────────────────────────
  const filteredCurrencies = useMemo(() => {
    return currencies.filter((curr) => {
      // Status filter
      if (statusFilter === 'ACTIVE' && !curr.isActive) return false;
      if (statusFilter === 'INACTIVE' && curr.isActive) return false;

      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.trim().toLowerCase();
        const matchesCode = curr.code.toLowerCase().includes(query);
        const matchesName = curr.name.toLowerCase().includes(query);
        const matchesSymbol = curr.symbol.toLowerCase().includes(query);
        return matchesCode || matchesName || matchesSymbol;
      }
      return true;
    });
  }, [currencies, statusFilter, searchQuery]);

  // Summary counts
  const totalCount = currencies.length;
  const activeCount = currencies.filter((c) => c.isActive).length;
  const inactiveCount = totalCount - activeCount;

  return (
    <AppLayout>
      <div className="p-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
          <div>
            <h1 className="text-xl font-bold text-[#1e293b] flex items-center gap-2.5">
              <span>Multi-Currency Settings</span>
              <span className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                CRUD Enabled
              </span>
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Configure supported foreign currencies, symbols, and exchange rates against base currency (INR)
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => fetchCurrencies(true)}
              disabled={refreshing}
              className="flex items-center gap-1.5 h-9 px-3.5 text-sm font-medium text-slate-700 bg-white border border-[#e2e8f0] hover:bg-slate-50 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
              title="Refresh currency list"
            >
              <i className={`ri-refresh-line text-base ${refreshing ? 'animate-spin text-indigo-600' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 h-9 px-4 bg-[#4f46e5] hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line text-base" />
              <span>Add Currency</span>
            </button>
          </div>
        </div>

        {/* Summary KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Currencies</p>
              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                <i className="ri-money-dollar-circle-line text-lg" />
              </div>
            </div>
            <p className="text-2xl font-bold text-[#1e293b] mt-2">{totalCount}</p>
            <p className="text-xs text-slate-400 mt-1">Configured in system</p>
          </div>

          <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Active Pairs</p>
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                <i className="ri-checkbox-circle-line text-lg" />
              </div>
            </div>
            <p className="text-2xl font-bold text-emerald-600 mt-2">{activeCount}</p>
            <p className="text-xs text-slate-400 mt-1">Available for transactions</p>
          </div>

          <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Inactive</p>
              <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-500">
                <i className="ri-pause-circle-line text-lg" />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-600 mt-2">{inactiveCount}</p>
            <p className="text-xs text-slate-400 mt-1">Temporarily disabled</p>
          </div>

          <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Base Currency</p>
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600">
                <i className="ri-bank-card-line text-lg" />
              </div>
            </div>
            <div className="flex items-baseline gap-2 mt-2">
              <p className="text-2xl font-bold text-indigo-600">INR</p>
              <span className="text-xs font-mono text-slate-500">(₹1.00)</span>
            </div>
            <p className="text-xs text-slate-400 mt-1">Indian Rupee (Standard)</p>
          </div>
        </div>

        {/* Filter & Search Bar */}
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 mb-5 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by code (e.g. USD), name, or symbol..."
              className="w-full pl-9 pr-4 py-2 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <i className="ri-close-circle-line text-sm" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-500 mr-1">Status:</span>
            <div className="inline-flex p-1 bg-slate-100 rounded-lg text-xs font-medium">
              <button
                onClick={() => setStatusFilter('ALL')}
                className={`px-3 py-1.5 rounded-md transition-colors cursor-pointer ${
                  statusFilter === 'ALL' ? 'bg-white text-[#1e293b] shadow-sm font-semibold' : 'text-slate-600 hover:text-[#1e293b]'
                }`}
              >
                All ({totalCount})
              </button>
              <button
                onClick={() => setStatusFilter('ACTIVE')}
                className={`px-3 py-1.5 rounded-md transition-colors cursor-pointer ${
                  statusFilter === 'ACTIVE' ? 'bg-white text-emerald-700 shadow-sm font-semibold' : 'text-slate-600 hover:text-[#1e293b]'
                }`}
              >
                Active ({activeCount})
              </button>
              <button
                onClick={() => setStatusFilter('INACTIVE')}
                className={`px-3 py-1.5 rounded-md transition-colors cursor-pointer ${
                  statusFilter === 'INACTIVE' ? 'bg-white text-slate-700 shadow-sm font-semibold' : 'text-slate-600 hover:text-[#1e293b]'
                }`}
              >
                Inactive ({inactiveCount})
              </button>
            </div>
          </div>
        </div>

        {/* Currency Table */}
        <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
          {loading ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-sm text-slate-500 mt-3">Loading currencies from database...</p>
            </div>
          ) : filteredCurrencies.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mx-auto mb-3">
                <i className="ri-money-dollar-circle-line text-2xl" />
              </div>
              <h3 className="text-sm font-semibold text-[#1e293b]">No currencies found</h3>
              <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                {searchQuery || statusFilter !== 'ALL'
                  ? 'No currencies match your search query or filter. Try clearing filters.'
                  : 'Get started by creating your first foreign currency.'}
              </p>
              {(searchQuery || statusFilter !== 'ALL') && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setStatusFilter('ALL');
                  }}
                  className="mt-4 px-3.5 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors cursor-pointer"
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 font-semibold text-[10px] tracking-wider uppercase">
                    <th className="px-5 py-2.5 whitespace-nowrap">Code</th>
                    <th className="px-5 py-2.5 whitespace-nowrap">Name</th>
                    <th className="px-5 py-2.5 whitespace-nowrap">Symbol</th>
                    <th className="px-5 py-2.5 text-right whitespace-nowrap">Rate (to INR)</th>
                    <th className="px-5 py-2.5 whitespace-nowrap">Last Updated</th>
                    <th className="px-5 py-2.5 text-center whitespace-nowrap">Status</th>
                    <th className="px-5 py-2.5 text-center whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredCurrencies.map((cur) => (
                    <tr
                      key={cur.id}
                      className={`hover:bg-slate-50/70 transition-colors ${
                        !cur.isActive ? 'bg-slate-50/30 opacity-75' : ''
                      }`}
                    >
                      {/* Code */}
                      <td className="px-5 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-[#1e293b] font-mono text-sm">{cur.code}</span>
                          {cur.isBaseCurrency && (
                            <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-indigo-100 text-indigo-700 rounded-md">
                              Base
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Name */}
                      <td className="px-5 py-4 whitespace-nowrap">
                        <span className="text-slate-700 font-medium">{cur.name}</span>
                      </td>

                      {/* Symbol */}
                      <td className="px-5 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-slate-100 font-semibold text-slate-700 font-mono text-sm">
                          {cur.symbol || '—'}
                        </span>
                      </td>

                      {/* Exchange Rate */}
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        <div className="flex flex-col items-end">
                          <span className="font-mono font-bold text-[#1e293b] text-sm">
                            ₹{cur.exchangeRate.toFixed(cur.exchangeRate < 1 ? 4 : 2)}
                          </span>
                          <span className="text-[11px] text-slate-400">
                            1 {cur.code} = ₹{cur.exchangeRate}
                          </span>
                        </div>
                      </td>

                      {/* Last Updated */}
                      <td className="px-5 py-4 whitespace-nowrap text-slate-500 text-xs">
                        <div className="flex items-center gap-1.5">
                          <i className="ri-calendar-line text-slate-400" />
                          <span>{cur.lastUpdated}</span>
                        </div>
                      </td>

                      {/* Status Toggle */}
                      <td className="px-5 py-4 text-center whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => handleToggleActive(cur)}
                          disabled={cur.isBaseCurrency}
                          title={cur.isBaseCurrency ? 'Base currency cannot be deactivated' : 'Click to toggle status'}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold cursor-pointer transition-all ${
                            cur.isActive
                              ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200/60'
                              : 'bg-slate-100 text-slate-500 hover:bg-slate-200 border border-[#e2e8f0]'
                          } ${cur.isBaseCurrency ? 'cursor-not-allowed opacity-90' : ''}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${cur.isActive ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                          <span>{cur.isActive ? 'Active' : 'Inactive'}</span>
                        </button>
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-4 text-center whitespace-nowrap">
                        <div className="flex items-center justify-center gap-1.5">
                          {/* Quick Rate Update */}
                          {!cur.isBaseCurrency && (
                            <button
                              type="button"
                              onClick={() => handleOpenRateModal(cur)}
                              className="px-2.5 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors cursor-pointer flex items-center gap-1"
                              title="Update Exchange Rate"
                            >
                              <i className="ri-refresh-line text-xs" />
                              <span className="hidden xl:inline">Rate</span>
                            </button>
                          )}

                          {/* Full Edit */}
                          <button
                            type="button"
                            onClick={() => handleOpenEdit(cur)}
                            className="p-1.5 text-slate-600 hover:text-indigo-600 hover:bg-slate-100 rounded-md transition-colors cursor-pointer"
                            title="Edit Currency Details"
                          >
                            <i className="ri-edit-line text-base" />
                          </button>

                          {/* Delete */}
                          {!cur.isBaseCurrency ? (
                            <button
                              type="button"
                              onClick={() => handleOpenDeleteModal(cur)}
                              className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors cursor-pointer"
                              title="Delete Currency"
                            >
                              <i className="ri-delete-bin-line text-base" />
                            </button>
                          ) : (
                            <span
                              className="p-1.5 text-slate-300 cursor-not-allowed"
                              title="Base currency cannot be deleted"
                            >
                              <i className="ri-lock-line text-base" />
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info Banner */}
        <div className="mt-6 p-4 bg-amber-50/80 border border-amber-200 rounded-xl shadow-sm">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 shrink-0 text-amber-700">
              <i className="ri-information-line text-lg" />
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-900">Exchange Rate Management & Base Currency Rules</p>
              <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                Exchange rates are maintained in PostgreSQL and utilized across Multi-Currency Purchase Orders, Sales Invoices, and Forex gain/loss tracking.
                The base currency is locked to <strong>INR</strong> with a fixed exchange rate of 1.0000. All GST statutory calculations and filing values are computed in INR.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Add Currency Modal ─────────────────────────────────────────────── */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                  <i className="ri-add-circle-line text-lg" />
                </div>
                <h3 className="text-base font-bold text-[#1e293b]">Add New Currency</h3>
              </div>
              <button
                onClick={() => setShowAddModal(false)}
                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <form onSubmit={handleAddCurrency}>
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-1.5">
                    Currency Code <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={newCurrency.code}
                    onChange={(e) => setNewCurrency({ ...newCurrency, code: e.target.value.toUpperCase() })}
                    placeholder="e.g. JPY, CAD, CHF"
                    maxLength={5}
                    className="w-full h-10 px-3.5 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-mono uppercase"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">ISO 4217 standard 3-letter currency code</p>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-1.5">
                    Currency Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={newCurrency.name}
                    onChange={(e) => setNewCurrency({ ...newCurrency, name: e.target.value })}
                    placeholder="e.g. Japanese Yen"
                    className="w-full h-10 px-3.5 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-1.5">
                      Symbol
                    </label>
                    <input
                      type="text"
                      value={newCurrency.symbol}
                      onChange={(e) => setNewCurrency({ ...newCurrency, symbol: e.target.value })}
                      placeholder="e.g. ¥, C$, CHF"
                      maxLength={10}
                      className="w-full h-10 px-3.5 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-1.5">
                      Exchange Rate (to INR) <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0.0001"
                      required
                      value={newCurrency.exchangeRate || ''}
                      onChange={(e) =>
                        setNewCurrency({ ...newCurrency, exchangeRate: parseFloat(e.target.value) || 0 })
                      }
                      placeholder="e.g. 0.55"
                      className="w-full h-10 px-3.5 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-mono"
                    />
                  </div>
                </div>

                <div className="pt-2">
                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={newCurrency.isActive}
                      onChange={(e) => setNewCurrency({ ...newCurrency, isActive: e.target.checked })}
                      className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
                    />
                    <span className="text-sm font-medium text-slate-700">Set as active immediately</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="h-9 px-4 text-sm font-medium text-slate-700 bg-white border border-[#e2e8f0] hover:bg-slate-50 rounded-lg transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="h-9 px-4 text-sm font-medium text-white bg-[#4f46e5] hover:bg-indigo-700 rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
                >
                  {submitting && <i className="ri-loader-4-line animate-spin" />}
                  <span>Add Currency</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Full Edit Currency Modal ───────────────────────────────────────── */}
      {editModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                  <i className="ri-edit-line text-lg" />
                </div>
                <h3 className="text-base font-bold text-[#1e293b]">
                  Edit Currency — {editModal.code}
                </h3>
              </div>
              <button
                onClick={() => setEditModal(null)}
                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <form onSubmit={handleSaveEdit}>
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-1.5">
                    Currency Code
                  </label>
                  <input
                    type="text"
                    disabled={editModal.isBaseCurrency}
                    value={editFormData.code}
                    onChange={(e) => setEditFormData({ ...editFormData, code: e.target.value.toUpperCase() })}
                    maxLength={5}
                    className="w-full h-10 px-3.5 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-mono uppercase disabled:bg-slate-100 disabled:text-slate-500"
                  />
                  {editModal.isBaseCurrency && (
                    <p className="text-[11px] text-indigo-600 mt-1 font-medium">Base currency code cannot be modified</p>
                  )}
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-1.5">
                    Currency Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    className="w-full h-10 px-3.5 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-1.5">
                      Symbol
                    </label>
                    <input
                      type="text"
                      value={editFormData.symbol}
                      onChange={(e) => setEditFormData({ ...editFormData, symbol: e.target.value })}
                      className="w-full h-10 px-3.5 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-1.5">
                      Exchange Rate (to INR)
                    </label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0.0001"
                      required
                      disabled={editModal.isBaseCurrency}
                      value={editModal.isBaseCurrency ? 1 : editFormData.exchangeRate || ''}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, exchangeRate: parseFloat(e.target.value) || 0 })
                      }
                      className="w-full h-10 px-3.5 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-mono disabled:bg-slate-100 disabled:text-slate-500"
                    />
                  </div>
                </div>

                {!editModal.isBaseCurrency && (
                  <div className="pt-2">
                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={editFormData.isActive}
                        onChange={(e) => setEditFormData({ ...editFormData, isActive: e.target.checked })}
                        className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
                      />
                      <span className="text-sm font-medium text-slate-700">Active status</span>
                    </label>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setEditModal(null)}
                  className="h-9 px-4 text-sm font-medium text-slate-700 bg-white border border-[#e2e8f0] hover:bg-slate-50 rounded-lg transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="h-9 px-4 text-sm font-medium text-white bg-[#4f46e5] hover:bg-indigo-700 rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
                >
                  {submitting && <i className="ri-loader-4-line animate-spin" />}
                  <span>Save Changes</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Quick Rate Modal ───────────────────────────────────────────────── */}
      {rateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="text-base font-bold text-[#1e293b]">Update Exchange Rate</h3>
              <button
                onClick={() => setRateModal(null)}
                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-1">
                  Currency
                </label>
                <div className="p-2.5 bg-slate-50 rounded-lg flex items-center justify-between">
                  <span className="font-bold text-[#1e293b]">{rateModal.code}</span>
                  <span className="text-xs text-slate-500">{rateModal.name}</span>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-1">
                  Current Rate
                </label>
                <p className="text-sm font-mono font-semibold text-slate-700">₹{rateModal.exchangeRate.toFixed(2)}</p>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-1.5">
                  New Rate (to INR) <span className="text-rose-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.0001"
                  min="0.0001"
                  autoFocus
                  value={newRateValue || ''}
                  onChange={(e) => setNewRateValue(parseFloat(e.target.value) || 0)}
                  className="w-full h-10 px-3.5 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-mono font-semibold"
                />
              </div>

              <div className="text-xs text-slate-500 flex items-center gap-1">
                <i className="ri-calendar-line text-slate-400" />
                <span>Update Date: {new Date().toISOString().split('T')[0]}</span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-5 py-4 bg-slate-50 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setRateModal(null)}
                className="h-9 px-4 text-sm font-medium text-slate-700 bg-white border border-[#e2e8f0] hover:bg-slate-50 rounded-lg transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveRate}
                disabled={submitting}
                className="h-9 px-4 text-sm font-medium text-white bg-[#4f46e5] hover:bg-indigo-700 rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
              >
                {submitting && <i className="ri-loader-4-line animate-spin" />}
                <span>Update Rate</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ──────────────────────────────────────── */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center text-rose-600 mx-auto mb-4">
                <i className="ri-error-warning-line text-2xl" />
              </div>
              <h3 className="text-base font-bold text-[#1e293b]">Delete Currency?</h3>
              <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                Are you sure you want to delete <strong className="text-slate-800">{deleteModal.code} ({deleteModal.name})</strong>?
                This currency will no longer appear in new transactions.
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setDeleteModal(null)}
                className="h-9 px-4 text-sm font-medium text-slate-700 bg-white border border-[#e2e8f0] hover:bg-slate-50 rounded-lg transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={submitting}
                className="h-9 px-4 text-sm font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
              >
                {submitting && <i className="ri-loader-4-line animate-spin" />}
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}